import React, { useState } from 'react';
import { Clock, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';

const CandidateTable = ({
    candidates,
    navigate,
    processingActions,
    setProcessingActions,
    sendingInterviews,
    setSendingInterviews,
    deletingCandidates,
    setDeletingCandidates,
    setCandidates,
    fetchCandidates,
    setAuthError,
    authError,
    getEmptyStateMessage,
    tableType = 'default', // 'public' or 'default'
    searchTerm = '', // Current search term
    advancedFilters = {}, // Current advanced filters
    currentPage = 1 // NEW: Current page number
}) => {
    const [confirmDelete, setConfirmDelete] = useState(null);

    // Define column configurations
    const columnConfigs = {
        public: [
            { key: 'candidateName', label: 'Name', className: '' },
            { key: 'email', label: 'Email', className: '' },
            { key: 'phone', label: 'Phone', className: '' },
            { key: 'experienceYears', label: 'Experience', className: '' },
            { key: 'currentCompany', label: 'Current Company', className: '' },
            { key: 'currentSalary', label: 'Current Salary', className: '' },
            { key: 'expectedSalary', label: 'Expected Salary', className: '' },
            { key: 'noticePeriod', label: 'Notice Period', className: '' },
            { key: 'preferredWorkMode', label: 'Work Mode', className: '' },
            { key: 'skillsMatchPercentage', label: 'Skills Match(%)', className: '' },
            { key: 'matchedRequiredSkills', label: 'Matched Skills', className: 'min-w-48' },
            { key: 'missingRequiredSkills', label: 'Missing Skills', className: 'min-w-48' },
            { key: 'keyStrengths', label: 'Key Strengths', className: 'min-w-48' },
            { key: 'reasoning', label: 'Remarks', className: 'min-w-48' },
            { key: 'matchScore', label: 'Score', className: '' },
            { key: 'status', label: 'Status', className: '' },
            { key: 'actions', label: 'Action', className: '' }
        ],
        default: [
            { key: 'candidateName', label: 'Name', className: '' },
            { key: 'email', label: 'Email', className: '' },
            { key: 'experienceYears', label: 'Experience', className: '' },
            { key: 'skillsMatchPercentage', label: 'Skills Match(%)', className: '' },
            { key: 'matchedRequiredSkills', label: 'Matched Skills', className: 'min-w-48' },
            { key: 'missingRequiredSkills', label: 'Missing Skills', className: 'min-w-48' },
            { key: 'phone', label: 'Phone', className: '' },
            { key: 'keyStrengths', label: 'Key Strengths', className: 'min-w-64' },
            { key: 'reasoning', label: 'Remarks', className: 'min-w-96' },
            { key: 'matchScore', label: 'Score', className: '' },
            { key: 'status', label: 'Status', className: '' },
            { key: 'actions', label: 'Action', className: '' }
        ]
    };

    const columns = columnConfigs[tableType] || columnConfigs.default;

    // Navigate to individual candidate dashboard
    const handleCandidateClick = (candidate) => {
        //console.log('Navigating to candidate:', candidate.candidateName);
        navigate(`/candidate/${candidate.id}/dashboard`);
    };

    // FIXED: Helper function to refresh current context
    const refreshCurrentView = (pageToRefresh = currentPage) => {
        //console.log(`🔄 Refreshing current view - Page: ${pageToRefresh}, Search: "${searchTerm}", Filters:`, advancedFilters);
        fetchCandidates(searchTerm, pageToRefresh, advancedFilters);
    };

    const handleReviewAction = async (candidateId, action, candidateName) => {
        const token = localStorage.getItem('adminToken');
        if (!token) {
            setAuthError('Authentication expired');
            navigate('/login', { replace: true });
            return;
        }

        const actionKey = `${candidateId}-${action}`;
        setProcessingActions(prev => new Set([...prev, actionKey]));

        try {
            const response = await fetch('https://n8n.acespireconsulting.com/webhook/hr-review-action', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    candidateId: candidateId,
                    action: action,
                    reviewedBy: 'archana.bansal@acespireconsulting.com',
                    reviewDate: new Date().toISOString(),
                    reviewNotes: `${action === 'accept' ? 'Approved' : 'Rejected'} via HR dashboard`
                })
            });

            if (response.status === 401 || response.status === 403) {
                setAuthError('Authentication failed during action');
                localStorage.removeItem('adminToken');
                localStorage.removeItem('adminUser');
                navigate('/login', { replace: true });
                return;
            }

            if (response.ok) {
                // Update local state immediately for better UX
                setCandidates(prev => prev.map(candidate => {
                    if (candidate.id === candidateId) {
                        return {
                            ...candidate,
                            filterDecision: action === 'accept' ? 'ACCEPT' : 'REJECT',
                            hrReviewStatus: action === 'accept' ? 'approved' : 'rejected',
                            reviewedAt: new Date().toISOString()
                        };
                    }
                    return candidate;
                }));

                const successMessage = `${candidateName} has been ${action === 'accept' ? 'accepted and will receive interview invitation' : 'rejected'}`;
                toast.success(successMessage);

                // FIXED: Refresh current context instead of always going to page 1
                setTimeout(() => {
                    refreshCurrentView();
                }, 1000);
            }
        } catch (error) {
            toast.error(`Failed to ${action} candidate: ${error.message}`);
        } finally {
            setProcessingActions(prev => {
                const newSet = new Set(prev);
                newSet.delete(actionKey);
                return newSet;
            });
        }
    };

    const handleSendInterviewInvite = async (candidateId, candidateName) => {
        const token = localStorage.getItem('adminToken');
        if (!token) {
            setAuthError('Authentication expired');
            navigate('/login', { replace: true });
            return;
        }

        const interviewKey = `interview-${candidateId}`;
        setSendingInterviews(prev => new Set([...prev, interviewKey]));

        try {
            const response = await fetch('https://n8n.acespireconsulting.com/webhook/send-interview-invite', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    candidateId: candidateId,
                    triggeredBy: 'hr@company.com',
                    triggeredAt: new Date().toISOString(),
                    action: 'send-interview-invite'
                })
            });

            if (response.status === 401 || response.status === 403) {
                setAuthError('Authentication failed during interview invite');
                localStorage.removeItem('adminToken');
                localStorage.removeUser('adminUser');
                navigate('/login', { replace: true });
                return;
            }

            if (response.ok) {
                toast.success(`Interview invitation sent to ${candidateName}!`);

                // FIXED: Refresh current context instead of always going to page 1
                setTimeout(() => {
                    refreshCurrentView();
                }, 1000);
            } else {
                toast.error(`Failed to send interview invite: ${response.statusText}`);
            }
        } catch (error) {
            toast.error(`Failed to send interview invite: ${error.message}`);
        } finally {
            setSendingInterviews(prev => {
                const newSet = new Set(prev);
                newSet.delete(interviewKey);
                return newSet;
            });
        }
    };

    const handleDeleteCandidate = async (candidateId, candidateName) => {
        const token = localStorage.getItem('adminToken');
        if (!token) {
            setAuthError('Authentication expired');
            navigate('/login', { replace: true });
            return;
        }

        const deleteKey = `delete-${candidateId}`;
        setDeletingCandidates(prev => new Set([...prev, deleteKey]));

        try {
            const response = await fetch('https://n8n.acespireconsulting.com/webhook/delete', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    candidateID: candidateId,
                    deletedBy: 'hr@company.com',
                    deletedAt: new Date().toISOString()
                })
            });

            if (response.status === 401 || response.status === 403) {
                setAuthError('Authentication failed during delete');
                localStorage.removeItem('adminToken');
                localStorage.removeItem('adminUser');
                navigate('/login', { replace: true });
                return;
            }

            if (response.ok) {
                toast.success(`${candidateName} has been deleted successfully.`);
                
                // Update local state immediately for better UX
                setCandidates(prev => prev.filter(candidate => candidate.id !== candidateId));

                // FIXED: Refresh current context and handle page adjustment if needed
                setTimeout(() => {
                    // If we deleted the last candidate on the current page and we're not on page 1,
                    // go back one page
                    const remainingCandidates = candidates.filter(candidate => candidate.id !== candidateId);
                    if (remainingCandidates.length === 0 && currentPage > 1) {
                        refreshCurrentView(currentPage - 1);
                    } else {
                        refreshCurrentView();
                    }
                }, 500);
            } else {
                toast.error(`Failed to delete ${candidateName}: ${response.statusText}`);
            }
        } catch (error) {
            toast.error(`Failed to delete ${candidateName}: ${error.message}`);
        } finally {
            setDeletingCandidates(prev => {
                const newSet = new Set(prev);
                newSet.delete(deleteKey);
                return newSet;
            });
        }
    };

    const showDeleteConfirmation = (candidateId, candidateName) => {
        setConfirmDelete({ id: candidateId, name: candidateName });
    };

    const confirmDeleteAction = () => {
        if (confirmDelete) {
            handleDeleteCandidate(confirmDelete.id, confirmDelete.name);
            setConfirmDelete(null);
        }
    };

    const cancelDelete = () => {
        setConfirmDelete(null);
    };

    // Helper functions
    const formatArrayWithLineBreaks = (items) => {
        if (!items || !Array.isArray(items)) return 'N/A';
        return items.map((item, index) => (
            <div key={index} className="leading-relaxed">{item}</div>
        ));
    };

    const formatReasoningWithLineBreaks = (text) => {
        if (!text) return 'N/A';
        const sentences = text.split(/(?<=[.!?])\s+|,\s+(?=with|and|but|however|although)/);
        return sentences.map((sentence, index) => (
            <div key={index} className="leading-relaxed mb-1">
                {sentence.trim()}
            </div>
        ));
    };

    const getStatusColor = (decision) => {
        switch (decision) {
            case 'ACCEPT': return 'bg-[#678D38] text-[#ffff]';
            case 'UNDER REVIEW': return 'bg-blue-100 text-blue-800';
            case 'REJECT': return 'bg-[#D12E34] text-[#ffff]';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusText = (decision) => {
        switch (decision) {
            case 'ACCEPT': return 'Shortlisted';
            case 'UNDER REVIEW': return 'Under Review';
            case 'REJECT': return 'Rejected';
            default: return 'Unknown';
        }
    };

    const renderStatusOrActions = (candidate) => {
        const isUnderReview = candidate.filterDecision === 'UNDER REVIEW';
        const acceptKey = `${candidate.id}-accept`;
        const rejectKey = `${candidate.id}-reject`;
        const isAcceptProcessing = processingActions.has(acceptKey);
        const isRejectProcessing = processingActions.has(rejectKey);

        if (isUnderReview) {
            return (
                <div className="flex items-center gap-2">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleReviewAction(candidate.id, 'accept', candidate.candidateName);
                        }}
                        disabled={isAcceptProcessing}
                        className={`px-4 py-2 rounded text-sm font-medium transition-colors ${isAcceptProcessing
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-[#678D38] text-white rounded-[40px]'
                            }`}
                    >
                        {isAcceptProcessing ? 'Processing...' : 'Accept'}
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleReviewAction(candidate.id, 'reject', candidate.candidateName);
                        }}
                        disabled={isRejectProcessing}
                        className={`px-4 py-2 rounded text-sm font-medium transition-colors ${isRejectProcessing
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-[#D12E34] text-[#ffff] rounded-[40px]'
                            }`}
                    >
                        {isRejectProcessing ? 'Processing...' : 'Reject'}
                    </button>
                </div>
            );
        } else {
            return (
                <span className={`px-4 py-2 text-sm font-medium rounded-[40px] ${getStatusColor(candidate.filterDecision)}`}>
                    {getStatusText(candidate.filterDecision)}
                </span>
            );
        }
    };

    const renderActionButtons = (candidate) => {
        const isAccepted = candidate.filterDecision === 'ACCEPT';
        const interviewKey = `interview-${candidate.id}`;
        const deleteKey = `delete-${candidate.id}`;
        const isSendingInterview = sendingInterviews.has(interviewKey);
        const isDeletingCandidate = deletingCandidates.has(deleteKey);

        return (
            <div className="flex items-center gap-2">
                {isAccepted && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleSendInterviewInvite(candidate.id, candidate.candidateName);
                        }}
                        disabled={isSendingInterview}
                        className={`p-1 transition-colors ${isSendingInterview
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-blue-500 hover:text-blue-700'
                            }`}
                        title={isSendingInterview ? 'Sending Chatbot link' : 'Send Chatbot link'}
                    >
                        {isSendingInterview ? (
                            <Clock className="w-4 h-4 animate-spin" />
                        ) : (
                            <img src="/assets/chatbot-link.png" alt="Interview Icon" className="w-6 h-6" />
                        )}
                    </button>
                )}
                
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        showDeleteConfirmation(candidate.id, candidate.candidateName);
                    }}
                    disabled={isDeletingCandidate}
                    className={`p-1 transition-colors ${isDeletingCandidate
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-[#D12E34] hover:text-[#D12E34]'
                        }`}
                    title={isDeletingCandidate ? 'Deleting candidate...' : 'Delete candidate'}
                >
                    {isDeletingCandidate ? (
                        <Clock className="w-4 h-4 animate-spin" />
                    ) : (
                        <Trash2 className="w-6 h-6" />
                    )}
                </button>
            </div>
        );
    };

    const renderCellContent = (candidate, column) => {
        const value = candidate[column.key];
        
        switch (column.key) {
            case 'candidateName':
                return <div className="text-sm font-medium text-[#202224]">{value}</div>;
            
            case 'email':
                return <div className="text-sm text-[#202224]">{value}</div>;
            
            case 'phone':
                return <div className="text-sm text-[#202224]">{value || 'N/A'}</div>;
            
            case 'experienceYears':
                return <div className="text-sm text-[#202224]">{value}</div>;
            
            case 'currentCompany':
                return <div className="text-sm text-[#202224]">{value || 'N/A'}</div>;
            
            case 'currentSalary':
                return <div className="text-sm text-[#202224]">{value || 'N/A'}</div>;
            
            case 'expectedSalary':
                return <div className="text-sm text-[#202224] font-medium">{value || 'N/A'}</div>;
            
            case 'noticePeriod':
                return <div className="text-sm text-[#202224]">{value || 'N/A'}</div>;
            
            case 'preferredWorkMode':
                return <div className="text-sm text-[#202224]">{value || 'N/A'}</div>;
            
            case 'skillsMatchPercentage':
                return <div className="text-sm text-[#202224]">{value}</div>;
            
            case 'matchedRequiredSkills':
                return (
                    <div className="text-sm text-[#202224]">
                        {value && value.length > 0 ? formatArrayWithLineBreaks(value) : "N/A"}
                    </div>
                );
            
            case 'missingRequiredSkills':
                return (
                    <div className="text-sm text-[#202224]">
                        {formatArrayWithLineBreaks(value)}
                    </div>
                );
            
            case 'keyStrengths':
                return (
                    <div className="text-sm text-gray-900">
                        {formatArrayWithLineBreaks(value)}
                    </div>
                );
            
            case 'reasoning':
                return (
                    <div className="text-sm text-gray-900">
                        {formatReasoningWithLineBreaks(value)}
                    </div>
                );
            
            case 'matchScore':
                return <div className="text-sm font-bold">{value}%</div>;
            
            case 'status':
                return renderStatusOrActions(candidate);
            
            case 'actions':
                return renderActionButtons(candidate);
            
            default:
                return <div className="text-sm text-[#202224]">{value || 'N/A'}</div>;
        }
    };

    return (
        <div className="flex-1 flex flex-col overflow-hidden m-2 rounded-lg bg-white shadow">
            {/* Table Container with Custom Scrollbar */}
            <div className="flex-1 overflow-auto custom-scrollbar">
                <table className="w-max min-w-full bg-white">
                    <thead className="bg-[#D4DEE8] border-b border-gray-200 sticky top-0 z-10">
                        <tr>
                            {columns.map((column) => (
                                <th 
                                    key={column.key}
                                    className={`px-6 py-3 text-left text-md font-medium text-[#202224] tracking-wider ${column.className}`}
                                >
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {candidates.map((candidate, index) => (
                            <tr
                                key={candidate.id || index}
                                className="border-b border-gray-200 hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleCandidateClick(candidate)}
                            >
                                {columns.map((column) => (
                                    <td 
                                        key={column.key}
                                        className={`px-6 py-4 ${column.className.includes('min-w') ? column.className : 'whitespace-nowrap'}`}
                                    >
                                        {renderCellContent(candidate, column)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>

                {candidates.length === 0 && !authError && (
                    <div className="py-12 text-center px-4">
                        <div className="text-gray-600 text-xl mb-2">{getEmptyStateMessage().title}</div>
                        <div className="text-md text-[#000000]">{getEmptyStateMessage().subtitle}</div>
                    </div>
                )}

                {authError && candidates.length === 0 && (
                    <div className="py-12 text-center px-4">
                        <div className="text-red-600 text-xl mb-2">Authentication Error</div>
                        <div className="text-md text-gray-600 mb-4">{authError}</div>
                        <button
                            onClick={() => navigate('/login')}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            Go to Login
                        </button>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {confirmDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50" onClick={cancelDelete}>
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl transform transition-all" onClick={(e) => e.stopPropagation()}>
                        <div className="text-center">
                            <div className="mb-6">
                                <p className="text-gray-600 mb-2">
                                    Do you want to delete this candidate?
                                </p>
                                <p className="text-lg font-semibold text-black p-3 rounded-md">
                                    {confirmDelete.name}
                                </p>
                            </div>

                            <div className="flex justify-center space-x-4">
                                <button
                                    onClick={cancelDelete}
                                    className="px-6 py-3 text-sm font-semibold text-gray-700 bg-gray-200 border border-gray-300 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDeleteAction}
                                    className="px-6 py-3 text-sm font-semibold text-white bg-[#D12E34] border border-transparent rounded-lg hover:bg-[#D12E34] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                                >
                                    Yes, Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CandidateTable;